import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Wifi, Search, Zap, Signal, CheckCircle2, ShoppingCart,
  ChevronRight, MapPin, Loader2, AlertCircle, Radio, Gauge,
  BatteryCharging, Antenna,
} from 'lucide-react'
import { productService } from '@/services/products'
import { checkCoverageOnly } from '@/services/networkCoverage'
import { useCartStore } from '@/stores/cartStore'
import { Product } from '@/types'
import SEOHead from '@/components/SEOHead'

// ─── Types ───────────────────────────────────────────────────────────────────

type ConnFilter = '4g' | '5g' | '4g5g'
type BbCategory = 'mifi' | 'lite' | 'premium' | 'battery' | 'antenna'

interface CoverageNetworkData {
  has_4g: boolean
  has_5g: boolean
  [key: string]: unknown
}

// ─── Constants ───────────────────────────────────────────────────────────────

const NETWORK_ORDER: Record<string, number> = { three: 1, o2: 2, vodafone: 3 }
const NETWORK_LABELS: Record<string, string> = { three: 'Three', o2: 'O2', vodafone: 'Vodafone' }
const NETWORK_COLORS: Record<string, string> = {
  three: 'bg-green-600',
  o2: 'bg-sky-500',
  vodafone: 'bg-red-600',
}

const CATEGORY_INFO: Record<BbCategory, {
  label: string
  icon: typeof Wifi
  color: string
  border: string
  bg: string
  specs: Record<ConnFilter, { speed: string; uses: string } | null>
}> = {
  mifi: {
    label: 'MiFi Portable',
    icon: Radio,
    color: 'text-purple-600',
    border: 'border-purple-200',
    bg: 'bg-purple-50',
    specs: {
      '4g':   { speed: '5–30 Mbps (estimated)', uses: 'Work, Email, General Use, Facebook' },
      '4g5g': { speed: '5–30 Mbps (estimated)', uses: 'Work, Email, General Use, Facebook' },
      '5g':   null, // MiFi is 4G-based; not shown for 5G-only
    },
  },
  lite: {
    label: 'Lite Broadband',
    icon: Wifi,
    color: 'text-blue-600',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
    specs: {
      '4g':   { speed: '10–50 Mbps (estimated)', uses: 'Email, Facebook, YouTube, Browsing, Limited TV Streaming' },
      '4g5g': { speed: '10–50 Mbps (estimated)', uses: 'Email, Facebook, YouTube, Streaming, Browsing and more' },
      '5g':   null, // Lite is 4G-based
    },
  },
  premium: {
    label: 'Premium Broadband',
    icon: Zap,
    color: 'text-green-600',
    border: 'border-green-200',
    bg: 'bg-green-50',
    specs: {
      '4g':   { speed: '50–150 Mbps (estimated)', uses: 'Facebook, Email, Netflix, TV Streaming, YouTube and more' },
      '4g5g': { speed: '80–400 Mbps (estimated)', uses: 'Streaming, Gaming, Facebook, Email, YouTube and much more' },
      '5g':   { speed: '80–400 Mbps (estimated)', uses: 'Streaming, Gaming, Facebook, Email, YouTube and much more' },
    },
  },
  battery: {
    label: 'Broadband Lite (Battery Option)',
    icon: BatteryCharging,
    color: 'text-amber-600',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    specs: {
      '4g':   { speed: '10–50 Mbps (estimated)', uses: 'Built-in Battery, Power-Cut Backup, Portable Use, Email, Facebook, Browsing' },
      '4g5g': { speed: '10–70 Mbps (estimated)', uses: 'Built-in Battery, Power-Cut Backup, Portable Use, Email, Streaming, Browsing' },
      '5g':   null, // Lite-based (4G)
    },
  },
  antenna: {
    label: 'Broadband Lite (External Antenna Option)',
    icon: Antenna,
    color: 'text-cyan-600',
    border: 'border-cyan-200',
    bg: 'bg-cyan-50',
    specs: {
      '4g':   { speed: '10–50 Mbps (estimated)', uses: 'External Antenna, Improved Signal, Rural & Weak-Signal Areas, Email, Facebook, Browsing' },
      '4g5g': { speed: '10–70 Mbps (estimated)', uses: 'External Antenna, Improved Signal, Rural & Weak-Signal Areas, Streaming, Browsing' },
      '5g':   null, // Lite-based (4G)
    },
  },
}

// Which categories are shown per connection filter
const VISIBLE_CATEGORIES: Record<ConnFilter, BbCategory[]> = {
  '4g':   ['lite', 'premium', 'battery', 'antenna', 'mifi'],
  '4g5g': ['lite', 'premium', 'battery', 'antenna', 'mifi'],
  '5g':   ['premium'],
}

// Some "Broadband Lite" products are distinguished only by their NAME (Battery Option /
// External Antenna) — they carry broadband_category=LITE (or none). Route them to their
// own guide section by name, and keep them OUT of the plain Lite section.
function guideCategory(p: Product): string {
  const n = (p.name || '').toLowerCase()
  if (n.includes('external antenna')) return 'antenna'
  if (n.includes('battery')) return 'battery'
  return p.broadband_category ?? ''
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Parse contract length from product name when the field is not set
function extractContractMonths(p: Product): number {
  const explicit = p.contract_length ?? p.contract_months
  if (explicit) return explicit
  const match = p.name.match(/(\d+)\s*[Mm]onth/)
  return match ? parseInt(match[1]) : 0
}

function sortProducts(products: Product[]): Product[] {
  return [...products].sort((a, b) => {
    // 1. Network priority (Three → O2 → Vodafone → others)
    const na = NETWORK_ORDER[a.network_type ?? ''] ?? 99
    const nb = NETWORK_ORDER[b.network_type ?? ''] ?? 99
    if (na !== nb) return na - nb
    // 2. Contract length descending (longest = cheapest monthly)
    const ca = extractContractMonths(a)
    const cb = extractContractMonths(b)
    if (cb !== ca) return cb - ca
    // 3. Price ascending
    return (a.price ?? 0) - (b.price ?? 0)
  })
}

// Detect 5G using Three, O2, Vodafone only (EE excluded — we don't sell EE).
// Three is the primary network. A network's 5G counts as confirmed only if it
// has non-null coverage data (indoor_coverage, outdoor_coverage, or data_speed).
// If any of our three networks has confirmed 5G, the area is treated as 5G capable.
function detectConnection(coverage: Record<string, CoverageNetworkData>): ConnFilter {
  const primaryNets = ['three', 'o2', 'vodafone']
  let has5g = false
  let any4g = false
  for (const net of primaryNets) {
    const data = coverage[net] as any
    if (!data) continue
    if (data.has_4g) any4g = true
    if (data.has_5g) {
      const confirmed = data.indoor_coverage != null || data.outdoor_coverage != null || data.data_speed != null
      if (confirmed) has5g = true
    }
  }
  if (has5g && any4g) return '4g5g'
  if (has5g) return '5g'
  return '4g'
}

function formatData(p: Product): string {
  if (!p.data_allowance_gb) return ''
  if (p.data_allowance_gb === -1) return 'Unlimited Data'
  return `${p.data_allowance_gb} GB Data`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProductCard({ product, onAdd, added }: {
  product: Product
  onAdd: (p: Product) => void
  added: boolean
}) {
  const monthly = product.price ?? 0
  const contractLen = extractContractMonths(product) || undefined
  const networkLabel = NETWORK_LABELS[product.network_type ?? ''] ?? product.network_type ?? ''
  const networkColor = NETWORK_COLORS[product.network_type ?? ''] ?? 'bg-gray-500'

  const isThree = product.network_type === 'three'

  return (
    <div className={`bg-white rounded-2xl border-2 shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col gap-3 ${isThree ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-200'}`}>
      {isThree && (
        <div className="flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1 w-fit">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Best Price · Recommended
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900 text-sm leading-tight">{product.name}</p>
          {formatData(product) && (
            <p className="text-xs text-gray-500 mt-0.5">{formatData(product)}</p>
          )}
        </div>
        <span className={`text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${networkColor}`}>
          {networkLabel}
        </span>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <span className="text-2xl font-bold text-gray-900">£{monthly.toFixed(2)}</span>
          <span className="text-xs text-gray-400">/mo</span>
          {contractLen && (
            <p className="text-xs text-gray-400">{contractLen}-month contract</p>
          )}
        </div>
        <button
          onClick={() => onAdd(product)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
            added
              ? 'bg-green-100 text-green-700'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {added ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          {added ? 'Added' : 'Add to Cart'}
        </button>
      </div>

      {product.features && product.features.length > 0 && (
        <ul className="space-y-0.5">
          {product.features.slice(0, 3).map((f, i) => (
            <li key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
              <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Pick cheapest 3 from Three, 3 from O2, 2 from Vodafone (already sorted by price asc within network)
function pickShowcase(all: Product[]): Product[] {
  const byNet = (net: string, limit: number) =>
    all.filter(p => p.network_type === net).slice(0, limit)
  return [
    ...byNet('three', 3),
    ...byNet('o2', 3),
    ...byNet('vodafone', 2),
  ]
}

function CategorySection({ category, connFilter, products, addedIds, onAdd }: {
  category: BbCategory
  connFilter: ConnFilter
  products: Product[]
  addedIds: Set<number>
  onAdd: (p: Product) => void
}) {
  const info = CATEGORY_INFO[category]
  const spec = info.specs[connFilter]
  const Icon = info.icon

  if (!spec) return null

  const all = products.filter(p => guideCategory(p) === category)
  if (all.length === 0) return null

  const visible = pickShowcase(all)
  // Battery/Antenna are name-derived pseudo-categories with no /broadband/:category page.
  const viewAllTo = (category === 'battery' || category === 'antenna') ? '/broadband' : `/broadband/${category}`

  return (
    <div className={`rounded-2xl border-2 ${info.border} overflow-hidden`}>
      {/* Category Header */}
      <div className={`${info.bg} px-5 py-5 flex flex-col sm:flex-row sm:items-start gap-5`}>
        {/* Left: title + speed */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
            <Icon className={`w-5 h-5 ${info.color}`} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-base">{info.label}</h3>
            <p className={`text-xs font-semibold ${info.color} mt-0.5`}>{spec.speed}</p>
          </div>
        </div>
        {/* Right: Application Guidelines */}
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-600 mb-3">Application Guidelines</p>
          <div className="flex flex-wrap gap-2">
            {spec.uses.split(', ').map(u => (
              <span key={u} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full">
                <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${info.color}`} />
                {u}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid — Three ×2, O2 ×1, Vodafone ×2 */}
      <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map(p => (
          <ProductCard key={p.id} product={p} onAdd={onAdd} added={addedIds.has(p.id)} />
        ))}
      </div>

      <div className="px-4 pb-4">
        <Link
          to={viewAllTo}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          View all {info.label} packages <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BroadbandGuide() {
  const [postcode, setPostcode] = useState('')
  const [postcodeInput, setPostcodeInput] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [coverageError, setCoverageError] = useState<string | null>(null)
  const [detectedConn, setDetectedConn] = useState<ConnFilter | null>(null)
  const [manualFilter, setManualFilter] = useState<ConnFilter | null>(null)
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set())
  const { addItem } = useCartStore()

  const connFilter: ConnFilter | null = manualFilter ?? detectedConn

  // Fetch all broadband products once coverage is checked
  const { data: products = [] } = useQuery({
    queryKey: ['broadband-guide-products'],
    queryFn: () => productService.getProducts({
      product_type: 'broadband',
      is_public: true,
      limit: 200,
    }),
    enabled: !!postcode,
    staleTime: 5 * 60 * 1000,
  })

  const sortedProducts = useMemo(() => sortProducts(products), [products])

  // Check coverage for postcode
  async function handleCheck(e: React.FormEvent) {
    e.preventDefault()
    const clean = postcodeInput.trim().toUpperCase()
    if (!clean) return
    setIsChecking(true)
    setCoverageError(null)
    setDetectedConn(null)
    setManualFilter(null)
    try {
      const result = await checkCoverageOnly(clean)
      const coverage = result.coverage as unknown as Record<string, CoverageNetworkData>
      if (!coverage || Object.keys(coverage).length === 0) {
        setCoverageError('No coverage data returned for this postcode. Please check and try again.')
        return
      }
      setPostcode(clean)
      setDetectedConn(detectConnection(coverage))
    } catch {
      setCoverageError('Could not check coverage. Please try again or check the postcode.')
    } finally {
      setIsChecking(false)
    }
  }

  function handleAdd(product: Product) {
    addItem(product)
    setAddedIds(prev => new Set([...prev, product.id]))
    setTimeout(() => {
      setAddedIds(prev => {
        const next = new Set(prev)
        next.delete(product.id)
        return next
      })
    }, 2000)
  }

  const visibleCategories: BbCategory[] = connFilter ? VISIBLE_CATEGORIES[connFilter] : []

  // Connection filter labels
  const connOptions: { id: ConnFilter; label: string; icon: typeof Signal; desc: string }[] = [
    { id: '4g', label: '4G Only', icon: Signal, desc: 'MiFi, Lite & Premium at 4G speeds' },
    { id: '4g5g', label: '4G & 5G Full Range', icon: Gauge, desc: 'Full range — best mix of price & speed' },
  ]

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 space-y-10">
      <SEOHead
        title="Broadband Buying Guide — Mobile, MiFi & Home | Silux Mobile"
        description="Not sure which broadband is right for you? Compare mobile broadband, MiFi, 4G/5G routers and home broadband, check coverage by postcode and find the best-value plan with the Silux Mobile broadband guide."
        canonical="https://www.siluxmobile.co.uk/broadband/guide"
      />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-950 via-slate-900 to-emerald-950 text-white px-6 sm:px-10 py-12 text-center border border-teal-900/50">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_50%,#5eead4,transparent_55%),radial-gradient(circle_at_75%_30%,#5eead4,transparent_55%)]" />
        <div className="relative space-y-4">
          <div className="inline-flex items-center gap-2 bg-teal-800/40 border border-teal-700/40 text-teal-300 rounded-full px-4 py-1.5 text-sm font-semibold">
            <Wifi className="w-4 h-4" />
            Broadband Selector Guide
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight text-white">
            Find the Right Broadband<br className="hidden sm:block" /> for Your Area
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Enter your postcode to check network coverage, then we'll show you the
            best packages available — sorted by price, starting from the cheapest.
          </p>
        </div>
      </div>

      {/* Postcode Check */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-indigo-600" />
          Step 1 — Check Your Coverage
        </h2>
        <p className="text-gray-500 text-sm mb-5">
          We'll check 4G and 5G availability for Three, O2, and Vodafone in your area.
        </p>

        <form onSubmit={handleCheck} className="flex flex-row gap-3 max-w-sm">
          <input
            type="text"
            value={postcodeInput}
            onChange={e => setPostcodeInput(e.target.value.toUpperCase())}
            placeholder="e.g. SW1A 1AA"
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-gray-900 font-medium tracking-wide"
            maxLength={8}
          />
          <button
            type="submit"
            disabled={isChecking || !postcodeInput.trim()}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors whitespace-nowrap flex-shrink-0"
          >
            {isChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {isChecking ? 'Checking...' : 'Check Coverage'}
          </button>
        </form>

        {coverageError && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {coverageError}
          </div>
        )}

        {postcode && detectedConn && (
          <div className="mt-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span>
              Coverage found for <strong>{postcode}</strong> —
              {detectedConn === '4g5g' && ' 4G & 5G available in your area.'}
              {detectedConn === '5g' && ' 5G available in your area.'}
              {detectedConn === '4g' && ' 4G coverage in your area (no 5G detected).'}
            </span>
          </div>
        )}
      </div>

      {/* Connection Type Filter */}
      {postcode && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Signal className="w-5 h-5 text-indigo-600" />
            Step 2 — Select Connection Type
          </h2>
          <p className="text-gray-500 text-sm mb-5">
            We've detected your coverage — but you can override it to compare different options.
          </p>

          <div className="grid sm:grid-cols-3 gap-3">
            {connOptions.map(({ id, label, icon: Icon, desc }) => {
              const isDetected = detectedConn === id
              const isSelected = (manualFilter ?? detectedConn) === id
              return (
                <button
                  key={id}
                  onClick={() => setManualFilter(id)}
                  className={`flex flex-col items-start gap-1.5 p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 bg-white hover:border-indigo-200'
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <span className={`font-bold text-sm ${isSelected ? 'text-indigo-700' : 'text-gray-700'}`}>
                        {label}
                      </span>
                    </div>
                    {isDetected && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">
                        Detected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{desc}</p>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Results */}
      {connFilter && products.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Step 3 — Recommended Packages
            </h2>
            <p className="text-sm text-gray-400 hidden sm:block">
              Cheapest first · Three → O2 → Vodafone
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
            <div>
              <p>Speeds shown are estimates, and vary based on various environmental and other factors that can influence it. We always recommend ordering and testing.</p>
              <p className="mt-1">You get a 14 Day Cooling Off period if you are not happy and wish to cancel. To cancel, use the online Cancel Contract page in your account or email info@siluxmobile.co.uk.</p>
            </div>
          </div>

          {visibleCategories.map(cat => (
            <CategorySection
              key={cat}
              category={cat}
              connFilter={connFilter}
              products={sortedProducts}
              addedIds={addedIds}
              onAdd={handleAdd}
            />
          ))}

          {visibleCategories.every(cat =>
            sortedProducts.filter(p => guideCategory(p) === cat).length === 0
          ) && (
            <div className="text-center py-12 text-gray-400">
              <Wifi className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No broadband packages found. Please check back soon.</p>
            </div>
          )}

          <div className="text-center pt-4">
            <Link
              to="/broadband"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition-colors"
            >
              View All Broadband Packages <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Loading products */}
      {postcode && connFilter && products.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-primary-400" />
          <p>Loading packages...</p>
        </div>
      )}

    </div>
  )
}
